import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import {
  IElement,
  IElementSection,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementGenericDataSession,
  isIElementGenericImgSheet,
  isIElementModel3dStream,
  isIElementPointCloudStream,
  isIElementSectionDataSession,
  isIElementWithTypeAndHint,
  WithHint,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectAllIElementsOfType,
  selectAncestor,
  selectChildDepthFirst,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/**
 * A memoized selector to find reference areas for cloud/cad alignment
 *
 * @param state The state of the app store
 * @param elementToAlign The element to be aligned
 * @returns The memoized selector
 */
export const selectReferenceAreas = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, elementToAlign: IElement) => elementToAlign,
    ],
    (iElements: IElementsState, elementToAlign: IElement) => {
      const state = { iElements };

      let areas;
      if (isIElementSectionDataSession(elementToAlign)) {
        const area = selectAncestor(
          elementToAlign,
          isIElementAreaSection,
        )(state);
        if (
          area &&
          selectChildDepthFirst(area, isIElementGenericImgSheet)(state)
        ) {
          areas = [area];
        }
      }

      if (isIElementModel3dStream(elementToAlign)) {
        const allAreas = selectAllIElementsOfType(isIElementAreaSection)(state);
        areas = allAreas.filter((area) =>
          selectChildDepthFirst(area, isIElementGenericImgSheet)(state),
        );
      }
      return areas;
    },
  ),
);

/**
 * A memoized selector to find reference point clouds for alignment
 *
 * @param state The state of the app store
 * @param elementToAlign The element to be aligned
 * @returns The memoized selector
 */
export const selectReferencePointClouds = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, elementToAlign: IElement) => elementToAlign,
    ],
    (iElements: IElementsState, elementToAlign: IElement) => {
      const state = { iElements };

      const area = isIElementSectionDataSession(elementToAlign)
        ? selectAncestor(elementToAlign, isIElementAreaSection)(state)
        : undefined;

      let pointClouds;
      if (area) {
        // If area is defined on previous step (eg. selected element is a cloud),
        // collect all other clouds belonging to the same area as reference (exclude elementToAlign).
        pointClouds = selectAllIElementsOfType(
          isIElementSectionDataSession,
          area.id,
        )(state).filter((cloud) => {
          const cloudStream = selectChildDepthFirst(
            cloud,
            isIElementPointCloudStream,
            3,
          )(state);
          return !!cloudStream && cloud.id !== elementToAlign.id;
        });
      } else if (isIElementAreaSection(elementToAlign)) {
        // If elementToAlign is an area, collect all geo-referenced clouds as reference
        pointClouds = selectAllIElementsOfType(
          (
            element,
          ): element is WithHint<
            IElementSection,
            IElementTypeHint.dataSession
          > =>
            isIElementWithTypeAndHint(
              element,
              IElementType.section,
              IElementTypeHint.dataSession,
            ),
        )(state);
      } else if (isIElementModel3dStream(elementToAlign)) {
        // If elementToAlign is a cad model, collect all point cloud datasets
        pointClouds = selectAllIElementsOfType(isIElementGenericDataSession)(
          state,
        );
      }
      return pointClouds;
    },
  ),
);
